/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC } from "react"
import { Button, Drawer, Radio } from "@duxton-web/ui"
import classNames from "classnames"

import styles from "./OptionPickerSheet.modules.css"

export interface PickerOption {
  value: string
  label: string
  /** Sub-label under the option. Only some option sets carry one. */
  helper?: string
}

/**
 * A row that sits below the options but is not one of them — a label with its own button
 * rather than a radio, so it never participates in the selection.
 */
export interface PickerSecondaryAction {
  label: string
  ctaLabel: string
  /** Renders the row inert — e.g. a once-per-item action that is already spent. */
  disabled?: boolean
  onPress(): void
}

interface OptionPickerSheetProps {
  open: boolean
  title: string
  options: PickerOption[]
  /** Currently committed value, if any. */
  value?: string
  secondaryAction?: PickerSecondaryAction
  onCancel(): void
  onSelect(value: string): void
}

/**
 * Bottom-sheet picker for narrow viewports, where the design replaces the dropdown with a radio
 * list. The caller chooses by breakpoint — this is a width concern, not a webview one, so a
 * narrowed desktop browser gets the sheet too.
 *
 * Tapping an option commits it and closes the sheet, so there is no `Save`: one tap instead of
 * two, and no staged state that could disagree with what the caller holds.
 */
export const OptionPickerSheet: FC<OptionPickerSheetProps> = ({
  open,
  title,
  options,
  value,
  secondaryAction,
  onCancel,
  onSelect,
}) => (
  <Drawer
    open={open}
    onClose={onCancel}
    placement="bottom"
    height="auto"
    closable={false}
    className={styles.sheet}
    /*
     * `body` zeroes Duxton's own `paddingLG`, which would otherwise stack with
     * `.content`'s inset and hold the rows ~44px off the edge instead of 20px — the
     * sibling `CategoryPickerSheet` zeroes it for the same reason.
     *
     * `wrapper` is a STYLE, not a className: Duxton rebuilds rc-drawer's class map with
     * only `mask` and `content` (`@duxton-web/ui/es/drawer/index.js`), so a
     * `classNames.wrapper` never reaches the element. It carries the drop shadow, which
     * would render square behind the rounded content.
     */
    styles={{
      body: { padding: 0 },
      wrapper: { borderRadius: "16px 16px 0 0" },
    }}
    /*
     * The sheet is a dialog with no visible chrome of its own (`closable` is false), so
     * without this it announces as an unnamed dialog. Its own heading is the name.
     */
    aria-label={title}
  >
    <div className={styles.content}>
      <h2 className={styles.title}>{title}</h2>
      <Radio.Group
        className={styles.group}
        value={value}
        onChange={event => onSelect(event.target.value)}
      >
        {options.map(option => (
          /*
           * `Radio` renders its own `<label>` (`@duxton-web/ui/es/radio/radio.js`), so it
           * owns the row: an outer label around it nested one inside the other, which is
           * invalid HTML and makes the hit target behave differently per browser and
           * screen reader. The row is reversed in CSS so the control still reads last.
           */
          <Radio
            className={styles.row}
            value={option.value}
            key={option.value}
            /*
             * A checked radio fires no `change`, so `Radio.Group`'s `onChange` never sees
             * a tap on the option that is ALREADY committed — the sheet would sit there
             * ignoring the merchant, which is exactly what someone reopening it to
             * confirm their choice does. Click covers that case; the guard keeps it from
             * doubling up with `onChange` on every other option.
             */
            onClick={() => {
              if (option.value === value) {
                onSelect(option.value)
              }
            }}
          >
            <span className={styles.rowText}>
              <span className={styles.rowLabel}>{option.label}</span>
              {option.helper && (
                <span className={styles.rowHelper}>{option.helper}</span>
              )}
            </span>
          </Radio>
        ))}
      </Radio.Group>
      {secondaryAction && (
        /*
         * A label with its own BUTTON: the CTA is the tappable control — Duxton's
         * secondary type in the round shape IS the design's chip, and brings its
         * own disabled treatment — so the row can never read as a selectable
         * radio entry.
         */
        <div
          className={classNames(styles.secondaryRow, {
            [styles.secondaryRowDisabled]: secondaryAction.disabled,
          })}
        >
          <span className={styles.rowLabel}>{secondaryAction.label}</span>
          <Button
            shape="round"
            type="secondary"
            // The default 40px control crowds the compact rows; small (32px) sits flush.
            size="small"
            className={styles.secondaryCta}
            disabled={secondaryAction.disabled}
            onClick={secondaryAction.onPress}
          >
            {secondaryAction.ctaLabel}
          </Button>
        </div>
      )}
    </div>
  </Drawer>
)
